import {
  useFloating,
  hide,
  offset,
  size,
  autoPlacement,
  autoUpdate,
  UseFloatingOptions,
  UseFloatingReturn,
  FloatingContext,
  useInteractions,
  UseClickProps,
  useClick,
  useDismiss,
  UseDismissProps,
  useRole,
  UseRoleProps,
  useListNavigation,
  UseListNavigationProps,
  useTypeahead,
  UseTypeaheadProps,
  SizeOptions,
  OffsetOptions,
} from '@floating-ui/react';

interface UseDropdownFloatingProps extends UseFloatingOptions {
  /** @default 4 */
  offsetSize?: OffsetOptions;
  /** @default 4 */
  padding?: number;
  /** @default true */
  optionsMatchRefWidth?: boolean;
  maxHeight?: number;
  /**
   * floating 요소가 reference 영역을 넘어갈 경우 자동 위치 처리 사용 유무
   *  - 자동 위치 상/하만 적용됩니다.
   * @default true
   */
  autoPlacement?: boolean;
  sizeMiddlewareOptions?: SizeOptions;
}

/**
 * @desc Dropdown floating 메인 훅
 * - 반환값 https://floating-ui.com/docs/useFloating#return-value 참고
 * - middleware 기본 정의값을 사용하지 않을 경우 전체 middleware(https://floating-ui.com/docs/useFloating#middleware)를 재정의할 수 있습니다.
 */
export const useDropdownFloating = ({
  open,
  onOpenChange,
  offsetSize = 4,
  padding = 4,
  optionsMatchRefWidth = true,
  maxHeight,
  autoPlacement: autoPlacementProps = true,
  sizeMiddlewareOptions,
  middleware,
}: UseDropdownFloatingProps): UseFloatingReturn => {
  return useFloating({
    open,
    onOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: middleware ?? [
      offset(offsetSize),
      size({
        ...(sizeMiddlewareOptions?.apply
          ? { apply: sizeMiddlewareOptions.apply }
          : {
              apply({ rects, elements, availableWidth, availableHeight }) {
                Object.assign(elements.floating.style, {
                  width: optionsMatchRefWidth ? `${rects.reference.width}px` : undefined,
                  maxWidth: !optionsMatchRefWidth ? `${availableWidth}px` : undefined,
                  maxHeight: maxHeight && `${availableHeight < maxHeight ? availableHeight - 20 : maxHeight}px`,
                });
              },
            }),
      }),
      ...(autoPlacementProps
        ? [autoPlacement({ allowedPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'], padding })]
        : []),
      hide(),
    ],
  });
};

export type DropdownInteraction = 'useClick' | 'useDismiss' | 'useRole' | 'useListNavigation' | 'useTypeahead';

type UseDropdownInteractionsProps = {
  context: FloatingContext;
  /**
   * 미사용할 이벤트 핸들러 목록
   */
  disabledInteractions?: DropdownInteraction[];
  useClickProps?: UseClickProps;
  useDismissProps?: UseDismissProps;
  useRoleProps?: UseRoleProps;
  useListNavigationProps?: UseListNavigationProps;
  useTypeaheadProps?: UseTypeaheadProps;
};

/**
 * @desc Dropdown 공통 이벤트 핸들러 훅
 * - 반환값 https://floating-ui.com/docs/useInteractions#return-value 참고
 */
export const useDropdownInteractions = ({
  context,
  disabledInteractions = [],
  useClickProps,
  useDismissProps,
  useRoleProps,
  useListNavigationProps,
  useTypeaheadProps,
}: UseDropdownInteractionsProps) => {
  const mergedUseClickProps: UseClickProps = {
    enabled: !disabledInteractions.includes('useClick'),
    event: 'mousedown',
    ...useClickProps,
  };
  const mergedUseRoleProps: UseRoleProps = {
    enabled: !disabledInteractions.includes('useRole'),
    role: 'listbox',
    ...useRoleProps,
  };
  // @ts-expect-error: TS2322
  const mergedUseListNavigationProps: UseListNavigationProps = {
    enabled: !disabledInteractions.includes('useListNavigation'),
    ...useListNavigationProps,
  };

  return useInteractions([
    useClick(context, mergedUseClickProps),
    useDismiss(context, { enabled: !disabledInteractions.includes('useDismiss'), ...useDismissProps }),
    useRole(context, mergedUseRoleProps),
    useListNavigation(context, mergedUseListNavigationProps),
    // @ts-expect-error: TS2345
    useTypeahead(context, { enabled: !disabledInteractions.includes('useTypeahead'), ...useTypeaheadProps }),
  ]);
};
